import { createFileRoute } from "@tanstack/react-router";
import AgenciaSeo from "@/pages/AgenciaSeo";
import { buildBreadcrumb, ldJson } from "@/lib/site-schema";

const canonicalUrl = "https://agenciaseoevolution.com/agencia-seo";

const faqs = [
  {
    question: "¿Qué hace exactamente una agencia SEO?",
    answer:
      "Una agencia SEO ejecuta el trabajo técnico, editorial y de autoridad necesario para que una web aparezca en las primeras posiciones de Google y en las respuestas de los motores generativos (ChatGPT, Perplexity, Gemini, Atlas). Eso incluye auditoría técnica, keyword research, optimización on-page, creación de contenidos, link building, mejoras de Core Web Vitals y reporting mensual de resultados.",
  },
  {
    question: "¿Cuánto cobra una agencia SEO en España en 2026?",
    answer:
      "Depende del sector, la competencia, el alcance y los objetivos: no es lo mismo un negocio local que una marca nacional o un ecommerce. En SEOEVOLUTION no publicamos tarifas porque cada empresa necesita una estrategia propia; alcance e inversión se concretan tras el diagnóstico, con compromiso mínimo de 3 meses en SEO local y de 6 meses en GEO, SEO web y ecommerce, sin garantía de resultados dentro del mínimo, y exclusividad por sector y zona: tu competidor directo no puede contratarnos.",
  },
  {
    question: "¿Cómo elegir una agencia SEO sin equivocarse?",
    answer:
      "Cuatro filtros básicos: (1) que no garantice posiciones —quien lo hace, miente o usa técnicas de riesgo—; (2) que muestre auditoría técnica real antes de cotizar; (3) que mida facturación generada, no solo impresiones o clics; (4) que trabaje con un número limitado de clientes y exclusividad por sector y zona. Si una agencia llena su cartera con paquetes cerrados de bajo coste, no puede entregar trabajo profundo.",
  },
  {
    question: "¿Qué diferencia a SEOEVOLUTION del resto de agencias SEO?",
    answer:
      "Cuatro diferencias claras: (1) exclusividad por sector y zona —tu competidor directo no nos contrata—; (2) plazas limitadas para asegurar atención senior; (3) reporting mensual de facturación, no de vanity metrics; (4) integración nativa de IA y GEO (motores generativos) en cada proyecto. Cada proyecto tiene un responsable directo, prioridades claras y ejecución documentada.",
  },
  {
    question: "¿Qué servicios SEO ofrece SEOEVOLUTION?",
    answer:
      "SEO Web con IA, SEO Local con IA, SEO Ecommerce con IA, GEO + SEO 360 (visibilidad en ChatGPT, Perplexity, Gemini y Atlas), creación web en React, creación de tiendas en Shopify, link building inteligente, publicidad SEM con IA, gestión de reseñas con IA y Google Merchant Center. Todos los servicios bajo modelo de exclusividad por sector y zona y plazas limitadas.",
  },
  {
    question: "¿Garantizáis aparecer en el top 3 de Google?",
    answer:
      "No. Cualquier agencia que garantice posiciones está mintiendo o usando técnicas de riesgo que terminan en penalización de Google. Lo que sí garantizamos es ejecutar el trabajo profesional que las primeras posiciones requieren: auditoría completa, contenido de autoridad, link building de calidad, optimización técnica continua y reporting transparente. Las posiciones llegan como consecuencia del trabajo bien hecho.",
  },
];

const businessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${canonicalUrl}#business`,
  name: "SEOEVOLUTION — Agencia SEO premium con IA",
  url: canonicalUrl,
  description:
    "Agencia SEO premium con Inteligencia Artificial en España. SEO Web, Local, Ecommerce y GEO. Exclusividad por sector y zona. Plazas limitadas. Propuesta personalizada tras diagnóstico.",
  telephone: "+34910626684",
  email: "contacto@agenciaseoevolution.com",
  areaServed: { "@type": "Country", name: "España" },
  address: {
    "@type": "PostalAddress",
    addressCountry: "ES",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "87",
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const breadcrumbSchema = buildBreadcrumb([{ name: "Agencia SEO", path: "/agencia-seo" }]);

export const Route = createFileRoute("/agencia-seo")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Agencia SEO con IA en España | SEOEVOLUTION" },
      {
        name: "description",
        content:
          "Agencia SEO con IA en España: estrategia, ejecución técnica, contenido y autoridad según tu empresa. SEO local, web y GEO con propuesta personalizada.",
      },
      {
        name: "keywords",
        content:
          "agencia SEO, agencia SEO España, agencia SEO premium, agencia SEO con IA, mejor agencia SEO, empresa SEO, consultora SEO",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: canonicalUrl },
      { property: "og:title", content: "Agencia SEO con IA en España | SEOEVOLUTION" },
      {
        property: "og:description",
        content:
          "Agencia SEO con IA en España: estrategia, ejecución técnica, contenido y autoridad según tu empresa. SEO local, web y GEO con propuesta personalizada.",
      },
      { name: "twitter:title", content: "Agencia SEO con IA en España | SEOEVOLUTION" },
      {
        name: "twitter:description",
        content:
          "Agencia SEO con IA en España: estrategia, ejecución técnica, contenido y autoridad según tu empresa. SEO local, web y GEO con propuesta personalizada.",
      },
    ],
    links: [{ rel: "canonical", href: canonicalUrl }],
    scripts: [
      ldJson(breadcrumbSchema),
      { type: "application/ld+json", children: JSON.stringify(businessSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  }),
  component: AgenciaSeo,
});
